import { ExcelGridColumn, ExcelGridRow } from "../../shared/excel-grid/models/excel-grid.models";

export const ADMIN_CERTIFICATES_SETTINGS_CATEGORY_OPTIONS = [
  "Certificates",
  "Questionnaires",
  "Other documents",
] as const;
export const ADMIN_CERTIFICATES_SETTINGS_KPI_OPTIONS = ["True", "False"] as const;

export const ADMIN_CERTIFICATES_SETTINGS_EDITABLE_FIELDS = [
  "KPI_INCLUDED",
  "EXPIRY_NOTICE_DAYS",
  "ENDORSEMENT_NOTICE_DAYS",
] as const;

export const ADMIN_CERTIFICATES_SETTINGS_NUMBER_FIELDS = ["EXPIRY_NOTICE_DAYS", "ENDORSEMENT_NOTICE_DAYS"] as const;

export type AdminCertificatesSettingsCategory = (typeof ADMIN_CERTIFICATES_SETTINGS_CATEGORY_OPTIONS)[number];
export type AdminCertificatesSettingsKpiOption = (typeof ADMIN_CERTIFICATES_SETTINGS_KPI_OPTIONS)[number];
export type AdminCertificatesSettingsEditableField = (typeof ADMIN_CERTIFICATES_SETTINGS_EDITABLE_FIELDS)[number];
export type AdminCertificatesSettingsNumberField = (typeof ADMIN_CERTIFICATES_SETTINGS_NUMBER_FIELDS)[number];
export type AdminCertificatesSettingsCancelHandler = () => void;
export type AdminCertificatesSettingsFieldChangeHandler = (
  field: AdminCertificatesSettingsEditableField,
  value: string,
) => void;
export type AdminCertificatesSettingsVesselTypesChangeHandler = (value: string[]) => void;

export interface AdminCertificatesSettingsPayload {
  EXCLUDED_VESSEL_TYPES: string[];
  KPI_INCLUDED: boolean;
  EXPIRY_NOTICE_DAYS: number | null;
  ENDORSEMENT_NOTICE_DAYS: number | null;
}

export type AdminCertificatesSettingsFormValues = Record<AdminCertificatesSettingsEditableField, string> & {
  EXCLUDED_VESSEL_TYPES: string[];
};

export type AdminCertificatesSettingsRow = ExcelGridRow &
  Omit<AdminCertificatesSettingsPayload, "EXCLUDED_VESSEL_TYPES"> & {
    ID: number;
    CERTIFICATE_NAME: string | null;
    CATEGORY: AdminCertificatesSettingsCategory | null;
    EXCLUDED_VESSEL_TYPES: string | null;
    VALIDITY_MONTHS: number | null;
    ENDORSEMENT_MONTHS: number | null;
    CHASER_DAYS: number | null;
    CREATED_AT?: string | null;
    UPDATED_AT?: string | null;
    UPDATED_BY?: string | null;
  };

export type AdminCertificatesSettingsEditableFieldsProps = {
  values: AdminCertificatesSettingsFormValues;
  vesselTypeOptions: string[];
  saving: boolean;
  onChange: AdminCertificatesSettingsFieldChangeHandler;
  onExcludedVesselTypesChange: AdminCertificatesSettingsVesselTypesChangeHandler;
};

export type AdminCertificatesSettingsExcludedVesselTypesFieldProps = {
  value: string[];
  options: string[];
  saving: boolean;
  onChange: AdminCertificatesSettingsVesselTypesChangeHandler;
};

export type AdminCertificatesSettingsKpiFieldProps = {
  value: string;
  saving: boolean;
  onChange: AdminCertificatesSettingsFieldChangeHandler;
};

export type AdminCertificatesSettingsModalActionsProps = {
  saving: boolean;
  onCancel: AdminCertificatesSettingsCancelHandler;
};

export type AdminCertificatesSettingsModalHeaderProps = {
  row: AdminCertificatesSettingsRow;
  saving: boolean;
  onCancel: AdminCertificatesSettingsCancelHandler;
};

export type AdminCertificatesSettingsTextFieldProps = {
  field: AdminCertificatesSettingsEditableField;
  value: string;
  saving: boolean;
  type?: "number" | "text";
  onChange: AdminCertificatesSettingsFieldChangeHandler;
};

export interface AdminCertificatesSettingsListResponse {
  rows: AdminCertificatesSettingsRow[];
  vesselTypeOptions: string[];
}

export interface AdminCertificatesSettingsMutationResponse {
  row: AdminCertificatesSettingsRow;
}

export const ADMIN_CERTIFICATES_SETTINGS_DATA_COLUMNS: ExcelGridColumn[] = [
  { key: "ID", label: "ID", editable: false, filterable: false, minWidth: 44, maxWidth: 48 },
  { key: "CERTIFICATE_NAME", label: "Certificate Name", editable: false, minWidth: 260, maxWidth: 340 },
  { key: "CATEGORY", label: "Category", editable: false, minWidth: 104, maxWidth: 120 },
  { key: "EXCLUDED_VESSEL_TYPES", label: "Excluded Vessel Types", editable: false, minWidth: 112, maxWidth: 134 },
  {
    key: "KPI_INCLUDED",
    label: "KPI Included",
    editable: false,
    serviceColumn: true,
    minWidth: 84,
    maxWidth: 96,
  },
  { key: "VALIDITY_MONTHS", label: "Validity Months", editable: false, minWidth: 92, maxWidth: 106 },
  { key: "EXPIRY_NOTICE_DAYS", label: "Expiry Notice Days", editable: false, minWidth: 92, maxWidth: 104 },
  { key: "ENDORSEMENT_MONTHS", label: "Endorsement Months", editable: false, minWidth: 96, maxWidth: 112 },
  {
    key: "ENDORSEMENT_NOTICE_DAYS",
    label: "Endorsement Notice Days",
    editable: false,
    minWidth: 118,
    maxWidth: 136,
  },
  { key: "CHASER_DAYS", label: "Chaser Days", editable: false, minWidth: 76, maxWidth: 88 },
  { key: "UPDATED_AT", label: "Updated At", editable: false, minWidth: 90, maxWidth: 110 },
  { key: "UPDATED_BY", label: "Updated By", editable: false, minWidth: 170, maxWidth: 190 },
];

export const getAdminCertificatesSettingsColumnLabel = (field: string): string =>
  ADMIN_CERTIFICATES_SETTINGS_DATA_COLUMNS.find((column) => column.key === field)?.label ??
  field.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
